import FormatDate from '@/components/FormatDate/FormatDate';
import {getReactiveContent, getStripHtmlTagsStr, getStyledTitle} from "@/lib/utils/helpers";
import classNames from 'classnames';
import PropTypes from "prop-types";
import Dropdown from "@/components/Dropdown/Dropdown";
import ContentBlock from "@/components/ContentBlock/ContentBlock";
import PostActions from "@/components/PostActions/PostActions";
import {gql} from "@apollo/client";
// import IconClock from "@/lib/assets/images/clock.svg";
import PostAuthorImage from "@/components/PostAuthorImage/PostAuthorImage";
import PostAuthor from "@/components/PostAuthor/PostAuthor";
import Link from "next/link";
import { Clock3 } from 'lucide-react';

export default function PostInfo({className, post, align = 'left', hideTOC = false}) {
  const {
    content,
    date,
    modified,
    momPostGenre: postGenre,
    // momAuthorsHtml: authors, // No longer used
    momReviewersHtml: reviewers,
    momRawExcerpt: rawExcerpt,
    momReadingTime: readingTime,
    momPostDisclaimer: postDisclaimer,
    momPublicAuthors: publicAuthors
  } = post;

  if (!date && !publicAuthors?.length) {
    return null;
  }

  // Align
  const isCenter = align === 'center';

  // Use modified date for "Updated:" display, fall back to published date
  const displayDate = modified && new Date(modified) >= new Date(date) ? modified : date;

  // Author
  const {
    fullName,
    avatarUrlSmall,
    uri,
  } = publicAuthors[0] || { uri: '#' };

  return (
    <>
      <div className={classNames(className)}>
        <div className={classNames('flex', {
          'justify-center': isCenter,
        })}>
          <Link href={uri} prefetch={false} className={`mr-3`}>
            <PostAuthorImage size={45} alt={fullName} avatarUrl={avatarUrlSmall} className={`mt-1`}/>
          </Link>

          <div>
            <strong>
              {fullName && (
                <>By <Link href={uri} prefetch={false}>{fullName}</Link></>
              )}{postGenre && (<> - {postGenre}</>)}
            </strong>

            <p className={classNames('lg:mt-1 font-serif text-[15px]')} suppressHydrationWarning>
              {date?.length > 0 ? (
                <>Updated: <FormatDate date={displayDate} format="d MMMM, yyyy" /></>
              ) : 'Unpublished'}
            </p>

            {reviewers && (<p className={classNames('mt-2.5 mb-0')}>{getReactiveContent({htmlString: reviewers})}</p>)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mt-5 mb-4 lg:mb-5">
          <PostActions className={classNames({
            'justify-center': isCenter,
            'md:col-span-12': hideTOC,
            'md:col-span-6': !hideTOC,
          })} storyId={post.databaseId}/>

          {!hideTOC && content.includes('wp-block-yoast-seo-table-of-contents') && (
            <Dropdown heading={'Table of contents'} className={`md:col-span-4`}>
              <ContentBlock 
                htmlString={content} 
                classToExtract={'wp-block-yoast-seo-table-of-contents'}
                onClick={(e) => {
                  // Check if clicked element is a link
                  if (e.target.tagName === 'A') {
                    e.preventDefault();
                    const targetId = e.target.getAttribute('href')?.replace('#', '');
                    
                    // Add SSR guard for document access
                    if (typeof window !== 'undefined') {
                      const targetElement = document.getElementById(targetId);
                      
                      if (targetElement) {
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }
                }}
              />
            </Dropdown>
          )}
        </div>

        {readingTime && (
          <p className={classNames(`font-bold mb-4 lg:mb-5`, {
            'text-center': isCenter,
          })}>
            <Clock3 className="inline-block mr-1 mb-0.5" /> Readtime: {readingTime}
          </p>
        )}

        {rawExcerpt && (
          <>
            <p className={`font-sans text-xxs font-bold uppercase tracking-widest`}>The Lowdown:</p>
            <p className={`lg:text-[23px] font-serif`}>
              {getStyledTitle({
                title: getStripHtmlTagsStr(rawExcerpt),
                italicLast: false,
                delimiters: [':', '  ', "\n"]
              })}
            </p>
          </>
        )}

        {postDisclaimer && (
          <div className={classNames(
            `mom-border border-y-2 py-3 mt-4`,
            `[&_p]:!leading-normal`,
            `[&_*]:text-xs [&_*]:!tracking-normal [&_*]:!text-black dark:[&_*]:!text-white`,
            {
              '[&_p]:!text-center': isCenter,
              '[&_p]:!text-left': !isCenter,
            }
          )}>
            {getReactiveContent({htmlString: postDisclaimer})}
          </div>
        )}
      </div>
    </>
  );
}

PostInfo.fragments = {
  key: 'PostInfoFragment',
  entry: gql`
    ${PostAuthor.fragments.entry}
    fragment PostInfoFragment on Post {
      date
      modified
      momPostGenre
      momReviewersHtml
      momRawExcerpt
      momReadingTime
      momPostDisclaimer
      momPublicAuthors {
        ...${PostAuthor.fragments.key}
      }
    }
  `
};

PostInfo.propTypes = {
  date: PropTypes.string,
  modified: PropTypes.string,
  authors: PropTypes.string,
  reviewers: PropTypes.string,
  postGenre: PropTypes.string,
  className: PropTypes.string
};
